import { Injectable } from '@angular/core';
import { GUID } from '@barthauer/abc/core';
import { Observable, Subject } from 'rxjs';
import { LoadProtocolTaskInfoSnapshot, TasksChangedEventArgs } from './load-protocol.model';

@Injectable({
  providedIn: 'root',
})
export class LoadProtocolService {
  private tasks: LoadProtocolTaskInfoSnapshot[] = [];
  private tasksSubject$: Subject<TasksChangedEventArgs> = new Subject<TasksChangedEventArgs>();

  constructor() {}

  public get onTasksChanged$(): Observable<TasksChangedEventArgs> {
    return this.tasksSubject$.asObservable();
  }

  private _keepFinishedTasksWhenNotBlocking: boolean;
  public get keepFinishedTasksWhenNotBlocking(): boolean {
    return this._keepFinishedTasksWhenNotBlocking;
  }

  public set keepFinishedTasksWhenNotBlocking(value: boolean) {
    if (this._keepFinishedTasksWhenNotBlocking !== value) {
      this._keepFinishedTasksWhenNotBlocking = value;

      if (!value && !this.hasRunningBlockingTasks) {
        this.removeFinishedTasks();
      }
    }
  }

  public addNewTask(description: string, isBlocking: boolean): LoadProtocolTaskInfoSnapshot {
    const newTask = new LoadProtocolTaskInfoSnapshot(GUID.newGUID(), description, isBlocking);
    this.addTask(newTask);

    return newTask;
  }

  private addTask(task: LoadProtocolTaskInfoSnapshot): void {
    this.tasks.push(task);
    this.publishTasks();
  }

  public finishTask(taskId: string, forceRemove: boolean = false): LoadProtocolTaskInfoSnapshot {
    let task;
    const taskIndex = this.tasks.findIndex((t) => t.id === taskId);

    if (taskIndex >= 0) {
      task = this.tasks[taskIndex];

      if (!task.isFinished) {
        task = task.asFinished();

        if (forceRemove) {
          this.tasks.splice(taskIndex, 1);
        } else {
          this.tasks.splice(taskIndex, 1, task);
        }
      } else if (forceRemove) {
        this.tasks.splice(taskIndex, 1);
      }

      if (!this.keepFinishedTasksWhenNotBlocking && !this.hasRunningBlockingTasks) {
        this.removeFinishedTasks(true);
      }

      this.publishTasks();
    } else {
      task = undefined;
    }

    return task;
  }

  public removeTask(taskId: string): LoadProtocolTaskInfoSnapshot {
    let task;
    const taskIndex = this.tasks.findIndex((t) => t.id === taskId);

    if (taskIndex >= 0) {
      task = this.tasks.splice(taskIndex, 1)[0];
      this.publishTasks();
    } else {
      task = undefined;
    }

    return task;
  }

  public clearTasks(): number {
    const oldTasksCount = this.tasks.length;

    if (oldTasksCount > 0) {
      this.tasks.length = 0;
      this.publishTasks();
    }

    return oldTasksCount;
  }

  public get hasRunningTasks(): boolean {
    return this.tasks.some((t) => !t.isFinished);
  }

  public get hasBlockingTasks(): boolean {
    return this.tasks.some((t) => t.isBlocking);
  }

  public get hasRunningBlockingTasks(): boolean {
    return this.tasks.some((t) => !t.isFinished && t.isBlocking);
  }

  private removeFinishedTasks(noPublish: boolean = false): void {
    let index = 0;
    let wereAnyRemoved = false;

    while (index < this.tasks.length) {
      if (this.tasks[index].isFinished) {
        this.tasks.splice(index, 1);
        wereAnyRemoved = true;
      } else {
        index++;
      }
    }

    if (wereAnyRemoved && !noPublish) {
      this.publishTasks();
    }
  }

  public publishTasks(): void {
    this.tasksSubject$.next(new TasksChangedEventArgs([...this.tasks])); // Do not publish original tasks ref!
  }
}
